// Street lighting from OpenStreetMap, kept in the app as small tiles (public/light): roads
// rasterised to ~20 m cells, each cell lit (tagged), likely lit (a 20 or 30 mph road or a
// residential street, which in the UK come with lights), not known, or unlit (tagged, or a
// path, track or 50 mph-plus road). A route is read every 25 m. Pure; the caller fetches tiles.
import { hav } from "./gpx.ts";

export type Light = { lit: number; likely: number; unknown: number; unlit: number; dark: [number, number, string[]][]; gaps: [number, number, string[]][] };
type Index = { tile: number; cell: [number, number]; rows: number; cols: number; tiles: string[]; data: string };
export type Tile = Uint8Array; // class per local cell, 0 none

export function decodeTile(buf: ArrayBuffer, cells: number): Tile {
  const v = new DataView(buf), out = new Uint8Array(cells);
  let o = 0;
  for (let code = 1; code <= 4; code++) {
    const n = v.getUint16(o, true); o += 2;
    for (let i = 0; i < n; i++) { out[v.getUint16(o, true)] = code; o += 2; }
  }
  return out;
}

export const tileName = (lat: number, lon: number, T: number) => `${Math.floor(lat / T)}_${Math.floor(lon / T)}`;

/** Every 25 m along the points, as [lat, lon, km]. */
export function sample(pts: [number, number][], step = 0.025): [number, number, number][] {
  const out: [number, number, number][] = [[pts[0][0], pts[0][1], 0]];
  let acc = 0, km = 0;
  for (let i = 1; i < pts.length; i++) {
    const d = hav(pts[i - 1], pts[i]); acc += d; km += d;
    while (acc >= step) { const t = d ? 1 - (acc - step) / d : 1; out.push([pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * t, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * t, km - (acc - step)]); acc -= step; }
  }
  return out;
}

/** The tiles a route touches, by name, so the caller can fetch just those. */
export function tilesFor(pts: [number, number][], ix: Index): string[] {
  const have = new Set(ix.tiles), s = new Set<string>();
  for (const [lat, lon] of sample(pts, 0.2)) for (const dy of [-0.001, 0.001]) for (const dx of [-0.0015, 0.0015]) { const n = tileName(lat + dy, lon + dx, ix.tile); if (have.has(n)) s.add(n); }
  return [...s];
}

/** Score a route. Own cell first, then the eight around it (about 20 m either side). */
export function scoreLight(pts: [number, number][], ix: Index, tiles: Record<string, Tile>): Light {
  const { tile: T, cell: [CY, CX], rows: R, cols: C } = ix;
  const at = (lat: number, lon: number) => {
    const ty = Math.floor(lat / T), tx = Math.floor(lon / T), t = tiles[`${ty}_${tx}`];
    if (!t) return 0;
    const r = Math.min(R - 1, Math.floor((lat - ty * T) / CY)), c = Math.min(C - 1, Math.floor((lon - tx * T) / CX));
    return t[r * C + c];
  };
  const PREC = [0, 4, 3, 1, 2]; // none, lit, likely, unknown, unlit: as the tiles were built
  const S = sample(pts), cls: number[] = [];
  for (const [lat, lon] of S) {
    let k = at(lat, lon);
    if (!k) for (const dy of [-CY, 0, CY]) for (const dx of [-CX, 0, CX]) { const j = at(lat + dy, lon + dx); if (PREC[j] > PREC[k]) k = j; }
    cls.push(k || 3);
  }
  const n = cls.length || 1, pc = (c: number) => Math.round((cls.filter((x) => x === c).length / n) * 100);
  const runs = (test: (c: number) => boolean, min: number): [number, number, string[]][] => {
    const out: [number, number, string[]][] = []; let a = -1;
    for (let i = 0; i <= cls.length; i++) {
      const bad = i < cls.length && test(cls[i]);
      if (bad && a < 0) a = i;
      if (!bad && a >= 0) { if ((i - a) * 25 >= min) out.push([Math.round(S[a][2] * 10) / 10, Math.round(S[i - 1][2] * 10) / 10, []]); a = -1; }
    }
    return out;
  };
  return { lit: pc(1), likely: pc(2), unknown: pc(3), unlit: pc(4), dark: runs((c) => c === 4, 200), gaps: runs((c) => c === 3, 300) };
}

/** Lit enough for a weekday evening: no unlit stretch, four fifths lit or 30 mph streets. His own survey overrides it. */
export const litEnough = (l: Light) => l.dark.length === 0 && l.unlit <= 2 && l.lit + l.likely >= 80;

/** Fetch the index and the tiles a route needs, from this site (`base` on the server). */
export async function lightFor(pts: [number, number][], get: (path: string) => Promise<ArrayBuffer | null>): Promise<(Light & { data: string }) | null> {
  const ib = await get("/light/index.json"); if (!ib) return null;
  const ix = JSON.parse(new TextDecoder().decode(ib)) as Index;
  const names = tilesFor(pts, ix); if (!names.length) return null;
  const tiles: Record<string, Tile> = {};
  await Promise.all(names.map(async (n) => { const b = await get(`/light/${n}.bin`); if (b) tiles[n] = decodeTile(b, ix.rows * ix.cols); }));
  return { ...scoreLight(pts, ix, tiles), data: ix.data };
}
